import { Fragment, useMemo, useState } from "react";
import { expandPrompt, PROMPT_GROUPS, pickN, type Scope } from "@/pages/ask/lib/data";
import Icon from "./Icon";

// ---------------------------------------------------------------------------
// PromptCard
// ---------------------------------------------------------------------------
function PromptCard({
  text,
  scope,
  accent,
  onClick,
}: {
  text: string;
  scope: Scope | null;
  accent: string;
  onClick: () => void;
}) {
  const [hover, setHover] = useState(false);

  // Highlight the scope name within the prompt text
  const scopeName = scope ? scope.displayName || scope.name : undefined;
  const parts = scopeName && text.includes(scopeName) ? text.split(scopeName) : null;

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        width: "100%",
        textAlign: "left",
        padding: "12px 14px",
        background: hover ? "#fff" : "var(--color-bg-secondary)",
        border: `1px solid ${hover ? "var(--color-brand-300)" : "var(--color-border-secondary)"}`,
        borderRadius: 10,
        cursor: "pointer",
        fontFamily: "inherit",
        fontSize: 14,
        lineHeight: "20px",
        color: "var(--color-text-secondary)",
        transition: "background 120ms, border-color 120ms, box-shadow 120ms",
        boxShadow: hover ? "var(--shadow-sm)" : "none",
        display: "flex",
        alignItems: "center",
        gap: 10,
      }}
    >
      <span style={{ flex: 1 }}>
        {parts
          ? parts.map((p, i) => (
              <Fragment key={i}>
                {i > 0 && <span style={{ fontWeight: 600, color: accent }}>{scopeName}</span>}
                {p}
              </Fragment>
            ))
          : text}
      </span>
      <Icon name="arrowUp" size={14} color={hover ? accent : "var(--color-gray-400)"} />
    </button>
  );
}

// ---------------------------------------------------------------------------
// PromptSection
// ---------------------------------------------------------------------------
function PromptSection({
  group,
  scope,
  onPickPrompt,
}: {
  group: (typeof PROMPT_GROUPS)[0];
  scope: Scope | null;
  onPickPrompt: (text: string) => void;
}) {
  // Pick 2 prompts for this category, stable for the lifetime of this mount
  // (re-randomized on a fresh page load). Keyed on group.id so it is computed once.
  const shown = useMemo(() => pickN(group.prompts, 2), [group.id]);

  return (
    <section>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 10,
        }}
      >
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: 8,
            background: group.accentBg,
            color: group.accent,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon name={group.icon as "trending" | "shield" | "users" | "play"} size={16} />
        </div>
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: "var(--color-text-primary)",
            letterSpacing: "-0.01em",
          }}
        >
          {group.label}
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {shown.map((p, i) => {
          const expanded = expandPrompt(p, scope);
          return (
            <PromptCard
              key={i}
              text={expanded}
              scope={scope}
              accent={group.accent}
              onClick={() => onPickPrompt(expanded)}
            />
          );
        })}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// PromptGrid
// ---------------------------------------------------------------------------
export default function PromptGrid({
  scope,
  onPickPrompt,
}: {
  scope: Scope | null;
  onPickPrompt: (text: string) => void;
}) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
        gap: 20,
        maxWidth: 820,
        margin: "0 auto",
      }}
    >
      {PROMPT_GROUPS.map((group) => (
        <PromptSection key={group.id} group={group} scope={scope} onPickPrompt={onPickPrompt} />
      ))}
    </div>
  );
}
